import { useState } from 'react';
import { NavLink, Link, Outlet, useLocation } from 'react-router-dom';
import { Menu, Phone, Mail, MapPin } from 'lucide-react';
import { useContent } from '../lib/content';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import { Logo } from './Logo';

const NAV = [
  { to: '/', label: 'HOME', testid: 'site-nav-home' },
  { to: '/trucks', label: 'TRUCKS FOR SALE', testid: 'site-nav-trucks' },
  { to: '/about', label: 'ABOUT US', testid: 'site-nav-about' },
  { to: '/contact', label: 'CONTACT', testid: 'site-nav-contact' },
];

export const PublicLayout = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { contact, footer } = useContent();

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <header className="sticky top-0 z-50 bg-[#24135F] border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden md:flex items-center gap-7" aria-label="Main navigation">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={item.testid}
                className={({ isActive }) =>
                  `text-[13px] font-semibold tracking-wider transition-colors duration-150 ${
                    isActive ? 'text-[#E4002B]' : 'text-white/85 hover:text-white'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <Button
              asChild
              data-testid="site-header-enquire-now-button"
              className="hidden sm:inline-flex rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-10 px-6 text-xs font-bold tracking-wider transition-colors duration-150"
            >
              <Link to="/contact">ENQUIRE NOW</Link>
            </Button>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-button"
                  className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors duration-150"
                  aria-label="Open menu"
                >
                  <Menu className="h-6 w-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-[#24135F] border-white/10 w-72">
                <div className="mt-8 flex flex-col gap-5">
                  {NAV.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setOpen(false)}
                      className={({ isActive }) =>
                        `text-base font-semibold tracking-wider ${isActive ? 'text-[#E4002B]' : 'text-white/90'}`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                  <Button
                    asChild
                    className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-11 font-bold tracking-wider mt-2"
                  >
                    <Link to="/contact" onClick={() => setOpen(false)}>
                      ENQUIRE NOW
                    </Link>
                  </Button>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1" key={location.pathname}>
        <Outlet />
      </main>

      <footer className="bg-[#24135F] text-white mt-auto">
        <div className="h-1 bg-[#E4002B]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <Logo />
            <p className="mt-4 text-sm text-white/60 leading-relaxed max-w-xs">{footer.blurb}</p>
          </div>
          <div>
            <h3 className="font-heading text-lg tracking-wider text-white mb-4">QUICK LINKS</h3>
            <ul className="space-y-2.5 text-sm">
              {NAV.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="text-white/60 hover:text-[#E4002B] transition-colors duration-150">
                    {item.label.charAt(0) + item.label.slice(1).toLowerCase()}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-lg tracking-wider text-white mb-4">CONTACT</h3>
            <ul className="space-y-3 text-sm text-white/60">
              <li className="flex items-center gap-3">
                <Phone className="h-4 w-4 text-[#E4002B]" /> {contact.phone}
              </li>
              <li className="flex items-center gap-3">
                <Mail className="h-4 w-4 text-[#E4002B]" /> {contact.email}
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 text-[#E4002B] mt-0.5" /> {contact.address}
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 text-xs text-white/40 flex flex-col sm:flex-row justify-between gap-2">
            <span>© {new Date().getFullYear()} Higgins Truck Sales. All rights reserved.</span>
            <span className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
              <span>Part of Higgins Services</span>
              <Link to="/privacy" className="text-white/60 hover:text-[#E4002B] underline underline-offset-2 transition-colors duration-150">
                Privacy Policy
              </Link>
              <span>
                Designed by{' '}
                <a
                  href="https://www.minchsolutions.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-designer-credit-link"
                  className="text-white/60 hover:text-[#E4002B] underline underline-offset-2 transition-colors duration-150"
                >
                  minchsolutions.au
                </a>
              </span>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};
